import {
  useCallback, useEffect, useId, useRef, useState,
  type TextareaHTMLAttributes,
} from 'react';
import { ListePropositions, usePropositions } from './propositions.tsx';

/**
 * Champ qui s'enregistre en frappant.
 *
 * L'application d'origine saisissait directement dans les cellules du tableau,
 * sans dialogue ni bouton « Enregistrer ». C'est ce qui rend tenable la
 * préparation d'une semaine : on remplit vingt cases à la suite sans jamais
 * quitter le clavier.
 *
 * ── Trois règles qui font la différence entre utilisable et pénible ──────────
 *
 * 1. **Le texte affiché appartient au champ, pas au parent.** Recevoir la
 *    valeur du serveur à chaque rendu ferait sauter le curseur en fin de ligne
 *    dès qu'une réponse arrive, au milieu d'une phrase. La valeur externe n'est
 *    reprise que si elle change alors qu'on n'écrit pas.
 *
 * 2. **La sortie du champ enregistre immédiatement.** Attendre le délai après
 *    un Tab laisserait une fenêtre où fermer l'onglet perd la frappe.
 *
 * 3. **L'état est visible.** Une saisie qui part « toute seule » inquiète tant
 *    qu'on ne voit pas qu'elle est partie. Un point discret suffit — et l'échec,
 *    lui, est annoncé.
 */

export type EtatEnregistrement = 'repos' | 'attente' | 'enregistre' | 'echec';

/*
 * ── Rien ne se perd en quittant ────────────────────────────────────────────
 * Le délai d'inactivité laisse une fenêtre : une phrase tapée, puis l'onglet
 * fermé ou la page quittée dans la seconde. Trois protections, communes à
 * tous les champs :
 *   — quand l'onglet passe en arrière-plan ou se ferme, chaque champ envoie
 *     aussitôt ce qui attendait ;
 *   — tant qu'une saisie attend ou est en route, fermer l'onglet demande
 *     confirmation au navigateur ;
 *   — un champ qui disparaît (changement de semaine, de page) envoie sa
 *     saisie au lieu de l'abandonner.
 */
let saisiesNonEnregistrees = 0;
const EVENEMENT_ENVOYER = 'journal:envoyer-saisies';
let gardeInstallee = false;

function installerGarde(): void {
  if (gardeInstallee || typeof window === 'undefined') return;
  gardeInstallee = true;
  const envoyerTout = () => window.dispatchEvent(new Event(EVENEMENT_ENVOYER));
  window.addEventListener('beforeunload', (e) => {
    envoyerTout();
    if (saisiesNonEnregistrees > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
  window.addEventListener('pagehide', envoyerTout);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') envoyerTout();
  });
}

/** Saisies qui attendent ou sont en route, tous champs confondus. */
export function saisiesEnAttente(): number {
  return saisiesNonEnregistrees;
}

export interface ProprietesSaisieDifferee
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  valeur: string;
  /** Appelée au repos après frappe, ou à la sortie du champ. */
  surEnregistrement: (valeur: string) => Promise<boolean> | boolean;
  /** Décrit le champ pour un lecteur d'écran. Obligatoire : il n'a pas de libellé visible. */
  libelle: string;
  /**
   * Délai d'inactivité avant enregistrement, en millisecondes.
   *
   * 700 ms : au-dessous, une frappe hésitante déclenche une requête par mot ;
   * au-dessus, on a le temps de fermer l'onglet en croyant avoir fini.
   */
  delai?: number;
  /** Grandit avec le contenu au lieu de faire défiler. */
  auto?: boolean;
  /**
   * Valeurs proposées à la saisie. Non contraignantes : on peut écrire autre
   * chose.
   *
   * Le champ devient alors une ligne unique munie d'une liste déroulante,
   * dessinée par l'application et collée au champ (voir `propositions.tsx`).
   * C'est le cas des champs
   * courts et répétitifs, « Français », « Lecture », qu'on retape sinon à
   * chaque séance, avec une faute de frappe tôt ou tard.
   */
  propositions?: string[];
}

export function SaisieDifferee({
  valeur, surEnregistrement, libelle, delai = 700, auto = true,
  propositions, className = '', onBlur, onInput, ...reste
}: ProprietesSaisieDifferee) {
  const [texte, setTexte] = useState(valeur);
  const [etat, setEtat] = useState<EtatEnregistrement>('repos');
  const champ = useRef<HTMLTextAreaElement>(null);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enCoursDeFrappe = useRef(false);
  const idEtat = useId();
  const ligneUnique = useRef<HTMLInputElement>(null);

  // Reprise de la valeur externe UNIQUEMENT hors frappe : un rechargement
  // pendant la saisie ne doit pas écraser ce qui est en train d'être écrit.
  useEffect(() => {
    if (!enCoursDeFrappe.current) setTexte(valeur);
  }, [valeur]);

  const ajusterHauteur = useCallback(() => {
    const el = champ.current;
    if (!auto || !el) return;
    // Remise à zéro d'abord : sans elle la hauteur ne fait que croître, même
    // quand on efface.
    el.style.height = 'auto';

    /*
     * ── Les BORDURES, sans quoi chaque champ est barré ────────────────────
     *
     * `scrollHeight` mesure la boîte de CONTENU. `height`, sous
     * `box-sizing: border-box` — la remise à zéro de Tailwind l'impose —
     * couvre en plus les bordures. Poser l'un dans l'autre laissait donc le
     * contenu deux pixels trop haut pour sa boîte : le navigateur affichait
     * une barre de défilement, sur CHAQUE zone de texte, y compris vide.
     *
     * Le défaut se voyait partout — cahier journal, progressions,
     * programmation — et nulle part on ne pouvait faire défiler quoi que ce
     * soit : il n'y avait rien de caché, seulement deux pixels manquants.
     */
    const style = getComputedStyle(el);
    const bordures = style.boxSizing === 'border-box'
      ? parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
      : 0;
    el.style.height = `${el.scrollHeight + bordures}px`;
  }, [auto]);

  useEffect(ajusterHauteur, [texte, ajusterHauteur]);

  /*
   * ── La hauteur dépend de la LARGEUR ────────────────────────────────────────
   *
   * Une zone de texte auto-dimensionnée mesure son contenu tel qu'il se replie
   * dans la largeur du moment. Or cette largeur change après coup : les
   * colonnes du tableau sont réglables, et l'enseignant en élargit une pour
   * lire un déroulement un peu long.
   *
   * Sans cet observateur, la hauteur restait celle calculée à la largeur
   * précédente : élargir la colonne laissait une zone trop haute, la rétrécir
   * en tronquait le texte. Le défaut ne se voyait qu'après avoir touché à une
   * poignée, donc rarement, et jamais au moment où l'on aurait pensé à le
   * chercher.
   *
   * `ResizeObserver` peut manquer dans un environnement de test : son absence
   * ne doit pas empêcher le champ de fonctionner.
   */
  useEffect(() => {
    const el = champ.current;
    if (!auto || !el || typeof ResizeObserver === 'undefined') return;
    const observateur = new ResizeObserver(ajusterHauteur);
    observateur.observe(el);
    return () => observateur.disconnect();
  }, [auto, ajusterHauteur]);

  // Lus au moment d'envoyer, pas au rendu : un envoi déclenché au démontage
  // ou par la fermeture de l'onglet doit voir la dernière valeur.
  const valeurConnue = useRef(valeur);
  valeurConnue.current = valeur;
  const rappel = useRef(surEnregistrement);
  rappel.current = surEnregistrement;
  const dernierTexte = useRef(valeur);
  const attenteComptee = useRef(false);

  const compterAttente = useCallback((oui: boolean) => {
    if (oui === attenteComptee.current) return;
    saisiesNonEnregistrees += oui ? 1 : -1;
    attenteComptee.current = oui;
  }, []);

  const enregistrer = useCallback(async (a: string) => {
    if (a === valeurConnue.current) { setEtat('repos'); return; }
    saisiesNonEnregistrees += 1;
    let ok = false;
    try {
      ok = await rappel.current(a);
    } catch {
      ok = false;
    } finally {
      saisiesNonEnregistrees -= 1;
    }
    setEtat(ok ? 'enregistre' : 'echec');
    if (ok) {
      // La pastille « enregistré » s'efface d'elle-même : la laisser en
      // permanence ferait un tableau constellé de points verts, qui ne
      // signaleraient plus rien.
      setTimeout(() => setEtat((e) => (e === 'enregistre' ? 'repos' : e)), 1500);
    }
  }, []);

  /** Envoie tout de suite ce qui attendait la fin du délai. */
  const envoyerMaintenant = useCallback(() => {
    if (!minuteur.current) return;
    clearTimeout(minuteur.current);
    minuteur.current = null;
    enCoursDeFrappe.current = false;
    compterAttente(false);
    void enregistrer(dernierTexte.current);
  }, [compterAttente, enregistrer]);

  /*
   * Choisir une proposition l'enregistre AUSSITÔT : c'est un choix fini, pas
   * une frappe en cours qu'il faudrait laisser mûrir.
   */
  const liste = usePropositions({
    propositions: propositions ?? [],
    texte,
    surChoix: (choisie) => {
      dernierTexte.current = choisie;
      setTexte(choisie);
      surSortie(choisie);
    },
  });

  /*
   * Enregistrement en attente au démontage : changer de semaine ou fermer un
   * dialogue ne doit pas emporter la dernière phrase. Le minuteur était
   * seulement ANNULÉ ici, ce qui jetait la saisie au lieu de l'envoyer.
   * Même envoi quand l'onglet passe en arrière-plan ou se ferme.
   */
  useEffect(() => {
    installerGarde();
    window.addEventListener(EVENEMENT_ENVOYER, envoyerMaintenant);
    return () => {
      window.removeEventListener(EVENEMENT_ENVOYER, envoyerMaintenant);
      envoyerMaintenant();
    };
  }, [envoyerMaintenant]);

  /** Enregistrement différé partagé par les deux variantes du champ. */
  function surFrappe(valeur_: string) {
    enCoursDeFrappe.current = true;
    dernierTexte.current = valeur_;
    setTexte(valeur_);
    setEtat('attente');
    compterAttente(true);
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => {
      minuteur.current = null;
      enCoursDeFrappe.current = false;
      compterAttente(false);
      void enregistrer(valeur_);
    }, delai);
  }

  /** Sortie du champ : ce qui attendait part aussitôt. */
  function surSortie(valeur_: string) {
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = null;
    enCoursDeFrappe.current = false;
    compterAttente(false);
    void enregistrer(valeur_);
  }

  const classesChamp = [
    'w-full resize-none rounded-md border border-transparent bg-transparent',
    'px-2 py-1.5 text-[13px] leading-snug',
    'hover:border-[var(--color-border)]',
    'focus:border-[var(--color-border-strong)] focus:bg-[var(--color-surface-input)]',
    'focus-visible:outline-none focus-visible:ring-2',
    'focus-visible:ring-[var(--color-focus)]',
    'transition-[border-color,background-color] duration-150',
    'motion-reduce:transition-none',
    etat === 'echec' ? 'border-[var(--color-danger)]' : '',
    className,
  ].join(' ');

  if (propositions && propositions.length > 0) {
    return (
      <div className="relative">
        <input
          ref={ligneUnique}
          type="text"
          value={texte}
          aria-label={libelle}
          autoComplete="off"
          {...liste.attributsChamp}
          onChange={(e) => { surFrappe(e.target.value); liste.ouvrir(); }}
          onFocus={() => liste.ouvrir()}
          onKeyDown={liste.surTouche}
          onBlur={(e) => {
            liste.fermer();
            surSortie(e.target.value);
          }}
          className={classesChamp}
          placeholder={reste.placeholder}
        />
        <ListePropositions
          ancre={ligneUnique}
          visibles={liste.visibles}
          affiche={liste.affiche}
          actif={liste.actif}
          idListe={liste.idListe}
          surChoix={liste.choisir}
        />

        {/* Jumeau imprimable : un champ de saisie ne s'imprime pas. */}
        <span className="impression-texte hidden text-[13px]">{texte}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <textarea
        ref={champ}
        value={texte}
        aria-label={libelle}
        aria-describedby={etat === 'echec' ? idEtat : undefined}
        rows={auto ? 1 : undefined}
        onChange={(e) => surFrappe(e.target.value)}
        onBlur={(e) => {
          surSortie(e.target.value);
          onBlur?.(e);
        }}
        onInput={(e) => { ajusterHauteur(); onInput?.(e); }}
        className={[
          'w-full resize-none rounded-md border border-transparent bg-transparent',
          'px-2 py-1.5 text-[13px] leading-snug',
          // La hauteur suit le contenu : il n'y a rien à faire défiler, et un
          // arrondi de sous-pixel ne doit pas ressusciter la barre.
          auto ? 'overflow-hidden' : '',
          'hover:border-[var(--color-border)]',
          'focus:border-[var(--color-border-strong)] focus:bg-[var(--color-surface-input)]',
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-[var(--color-focus)]',
          'transition-[border-color,background-color] duration-150',
          'motion-reduce:transition-none',
          etat === 'echec' ? 'border-[var(--color-danger)]' : '',
          className,
        ].join(' ')}
        {...reste}
      />

      {/*
        * Jumeau imprimable.
        *
        * Un `<textarea>` ne sort de l'imprimante que ce qui tenait dans sa
        * hauteur à l'écran ; le reste disparaît sans avertissement. La feuille
        * d'impression masque donc le champ et révèle ce paragraphe, qui porte
        * le texte entier. `aria-hidden` : à l'écran il est invisible, et
        * l'annoncer doublerait le contenu du champ pour un lecteur d'écran.
        */}
      <p aria-hidden="true" className="impression-texte hidden px-2 py-1.5 text-[13px]">
        {texte}
      </p>

      {/*
        * Témoin d'état, coin haut droit. `aria-hidden` sur les deux états
        * bénins : les annoncer ferait parler le lecteur d'écran à chaque
        * respiration. L'échec, lui, est un message à part entière.
        */}
      {etat === 'attente' && (
        <span
          aria-hidden="true"
          title="Enregistrement en attente"
          className="pointer-events-none absolute right-1.5 top-1.5 size-1.5 rounded-full bg-[var(--color-fg-muted)]"
        />
      )}
      {etat === 'enregistre' && (
        <span
          aria-hidden="true"
          title="Enregistré"
          className="pointer-events-none absolute right-1.5 top-1.5 size-1.5 rounded-full bg-[var(--color-success)]"
        />
      )}
      {etat === 'echec' && (
        <p id={idEtat} role="alert" className="mt-1 text-[11px] text-[var(--color-danger)]">
          Non enregistré. Votre texte est encore là : recliquez dans le champ
          pour réessayer.
        </p>
      )}
    </div>
  );
}
